import { useState, useEffect, useRef } from 'react';
import { ClientOnly, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { WORKERS } from '@/api/config';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { getMyEnrollmentsOptions, enrollInSessionGroupMutation } from '@/api/generated/@tanstack/react-query.gen';
import {
  Clock,
  BookOpen,
  Loader2,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { createLearnPageHead } from '@/lib/seo';
import { trackLearningPathStart } from '@/lib/analytics';
import { usePublicSessionMaterials, MaterialsSlider } from '@/components/Learn/MaterialsSlider';
import { StartLearningButton } from '@/components/Learn/StartLearningButton';
import { useAutoStartSession } from '@/hooks/useAutoStartSession';
import { AutumnProvider } from '@/components/AutumnProvider';
import { useCustomer } from 'autumn-js/react';
import { canStartSession } from '@/lib/plan';
import { EASE_OUT } from '@/components/Auth/AuthPageShell';
import { GreetLogo } from '@/components/brand/GreetLogo';

const { USERS_WORKER } = WORKERS;

// --- Animated prompts ---

const SAMPLE_PROMPTS = [
  'Linear algebra for ML enthusiasts',
  'Middle east wars in the 20th century',
  'ELI5 Rocket engines',
  'Explain quantum mechanics with visualizations',
];

// --- Types ---

interface SessionItem {
  id: string;
  name: string;
  duration: number;
  description: string;
}

interface SessionGroupDetail {
  id: string;
  name: string;
  description: string;
  type?: 'learning' | 'screening' | 'research';
  cover_image_url: string | null;
  learning_outcomes: string[];
  categories: string[];
  total_duration_minutes: number;
  session_count: number;
  generation_status: string;
  generated_session_count: number;
  target_session_count: number;
  public_id: string;
  sessions: SessionItem[];
  avg_rating: number | null;
  enrollment_count: number;
  owner_id: string | null;
}

interface Enrollment {
  session_group_id: string;
  completed_sessions: number;
  completed_session_template_ids: string[];
}

const groupDetailUrl = (publicId: string) =>
  `${USERS_WORKER}/public/learn/session-groups/${publicId}`;

async function fetchGroupDetail(publicId: string): Promise<SessionGroupDetail> {
  const res = await fetch(groupDetailUrl(publicId));
  if (!res.ok) throw new Error('Failed to fetch');
  return res.json();
}

const LEARN_REF_KEY = 'greetle_learn_ref';
const MAX_POLL_MS = 5 * 60 * 1000;

export const Route = createFileRoute('/learn/$publicId')({
  validateSearch: (search: Record<string, unknown>) => ({
    ref: (search.ref as string) || undefined,
    category: (search.category as string) || undefined,
  }),
  component: PublicLearningPathPage,
  loader: async ({ params }): Promise<{ group: SessionGroupDetail | null }> => {
    const res = await fetch(groupDetailUrl(params.publicId));
    return { group: res.ok ? await res.json() : null };
  },
  head: ({ loaderData, params }) => {
    const group = loaderData?.group;
    if (!group) return { meta: [{ name: 'title', content: 'Learning Path - GreetAI' }] };
    return createLearnPageHead({
      name: group.name,
      description: group.description || undefined,
      coverImageUrl: group.cover_image_url || undefined,
      publicId: params.publicId,
      sessionCount: group.session_count,
      totalDuration: group.total_duration_minutes,
    });
  },
});

// --- Animated Prompt Input Banner ---

function HeaderWithPrompt() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPromptIndex((prev) => (prev + 1) % SAMPLE_PROMPTS.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleGenerateClick = () => {
    if (isAuthenticated) {
      navigate({ to: '/chat' });
      return;
    }

    navigate({ to: '/register', search: { returnTo: '/chat' } });
  };

  return (
    <header className="sticky top-0 z-40 border-b border-black/5 bg-[#f0f0f3]/90 px-3 py-2.5 backdrop-blur-md dark:border-white/5 dark:bg-[#141416]/90 sm:px-4">
      <div className="mx-auto flex max-w-3xl items-center gap-2 sm:gap-3">
        <GreetLogo className="h-5 w-auto shrink-0" />

        <div
          className="relative flex h-9 flex-1 cursor-pointer items-center overflow-hidden rounded-xl bg-[#f0f0f3] shadow-[3px_3px_8px_rgba(0,0,0,0.06),-2px_-2px_6px_rgba(255,255,255,0.9)] dark:bg-[#1c1c1f] dark:shadow-[3px_3px_10px_rgba(0,0,0,0.45),-2px_-2px_6px_rgba(255,255,255,0.03)]"
          onClick={handleGenerateClick}
        >
          <div className="flex min-w-0 flex-1 items-center px-2.5 sm:px-3">
            <AnimatePresence mode="wait">
              <motion.span
                key={currentPromptIndex}
                className="truncate text-xs text-stone-400 sm:text-sm"
                initial={{ opacity: 0, transform: 'translateY(4px)' }}
                animate={{ opacity: 1, transform: 'translateY(0px)' }}
                exit={{ opacity: 0, transform: 'translateY(-4px)' }}
                transition={{ duration: 0.22, ease: EASE_OUT }}
              >
                {SAMPLE_PROMPTS[currentPromptIndex]}
              </motion.span>
            </AnimatePresence>
          </div>

          <div className="flex shrink-0 items-center gap-1 border-l border-black/5 px-2.5 dark:border-white/5 sm:px-3">
            <Sparkles className="h-3.5 w-3.5 text-stone-500" />
            <span className="bg-gradient-to-r from-stone-700 to-stone-500 bg-clip-text text-xs font-medium text-transparent sm:text-sm dark:from-stone-200 dark:to-stone-400">
              Generate
            </span>
          </div>
        </div>
      </div>
    </header>
  );
}

function AuthenticatedStartButton({
  nextSessionId,
  hasCompletedSessions,
  isEnrolling,
  ensureEnrollment,
}: {
  nextSessionId: string;
  hasCompletedSessions: boolean;
  isEnrolling: boolean;
  ensureEnrollment: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const { startSession, isStarting, startingSessionId, isCustomerLoading } = useAutoStartSession();
  const { customer } = useCustomer();
  const isStartingCurrentPath = startingSessionId === nextSessionId;

  if (!isCustomerLoading && !canStartSession(customer)) {
    return (
      <div className="space-y-2">
        <StartLearningButton onClick={() => navigate({ to: '/billing' })}>
          Upgrade to continue
        </StartLearningButton>
        <p className="text-center text-xs text-stone-500">You're out of sessions</p>
      </div>
    );
  }

  const handleClick = async () => {
    trackLearningPathStart();
    try {
      await ensureEnrollment();
      startSession(nextSessionId);
    } catch {
      // Enrollment/start failures are surfaced by useAutoStartSession / toast elsewhere
    }
  };

  return (
    <StartLearningButton
      onClick={handleClick}
      disabled={isEnrolling || isStarting || isCustomerLoading}
      isLoading={isEnrolling || isStartingCurrentPath || isCustomerLoading}
      loadingLabel={isCustomerLoading ? 'Checking access...' : 'Starting...'}
    >
      {hasCompletedSessions ? 'Continue learning' : 'Start learning'}
    </StartLearningButton>
  );
}

function StartLearningCta({
  group,
}: {
  group: SessionGroupDetail;
}) {
  return (
    <ClientOnly
      fallback={
        <StartLearningButton disabled>Start learning</StartLearningButton>
      }>
      <StartLearningCtaClient group={group} />
    </ClientOnly>
  );
}

function StartLearningCtaClient({
  group,
}: {
  group: SessionGroupDetail;
}) {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const { data: enrollments } = useQuery({
    ...getMyEnrollmentsOptions(),
    enabled: isAuthenticated,
  });

  const enrollMutation = useMutation(enrollInSessionGroupMutation());

  const existingEnrollment = Array.isArray(enrollments)
    ? (enrollments as Enrollment[]).find((enrollment) => enrollment.session_group_id === group.id)
    : null;
  const completedIds = new Set(existingEnrollment?.completed_session_template_ids || []);
  const nextSessionId = group.sessions.find((session) => !completedIds.has(session.id))?.id;

  if (!nextSessionId) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-green-200 bg-green-50/50 p-4">
        <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-600" />
        <p className="text-sm text-green-800">You've completed this course</p>
      </div>
    );
  }

  const handleAuthenticatedStart = async () => {
    if (existingEnrollment) {
      return;
    }

    let sourceAppId: string | undefined;
    try { sourceAppId = localStorage.getItem(LEARN_REF_KEY) || undefined; } catch { }

    await enrollMutation.mutateAsync({
      path: { public_id: group.public_id },
      body: { source_application_id: sourceAppId },
    });

    if (sourceAppId) {
      try { localStorage.removeItem(LEARN_REF_KEY); } catch { }
    }
  };

  if (!isAuthenticated || !user) {
    return (
      <StartLearningButton
        onClick={() => {
          trackLearningPathStart();
          navigate({
            to: '/register',
            search: { returnTo: `/learn/${group.public_id}` },
          });
        }}
      >
        Sign up to start
      </StartLearningButton>
    );
  }

  return (
    <AutumnProvider>
      <AuthenticatedStartButton
        nextSessionId={nextSessionId}
        hasCompletedSessions={Boolean(existingEnrollment?.completed_sessions)}
        isEnrolling={enrollMutation.isPending}
        ensureEnrollment={handleAuthenticatedStart}
      />
    </AutumnProvider>
  );
}

// --- Main Page ---

function PublicLearningPathPage() {
  const { publicId } = Route.useParams();
  const { ref: refParam } = Route.useSearch();
  const { group: loaderGroup } = Route.useLoaderData();
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    if (refParam) {
      try { localStorage.setItem(LEARN_REF_KEY, refParam); } catch { }
    }
  }, [refParam]);

  const generatingStartRef = useRef<number | null>(null);

  const { data: group, isLoading } = useQuery({
    queryKey: ['learn-group-detail-public', publicId],
    queryFn: () => fetchGroupDetail(publicId),
    initialData: loaderGroup || undefined,
    refetchInterval: (query) => {
      const status = query.state.data?.generation_status;
      if (status === 'planning' || status === 'generating') {
        if (!generatingStartRef.current) {
          generatingStartRef.current = Date.now();
        }
        if (Date.now() - generatingStartRef.current > MAX_POLL_MS) {
          return false;
        }
        return 3000;
      }
      generatingStartRef.current = null;
      return false;
    },
  });
  const materialsStatus = usePublicSessionMaterials(group?.sessions || []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f0f0f3] dark:bg-[#141416]">
        <div className="mx-auto w-full max-w-3xl space-y-6 px-4 py-6 sm:py-8">
          <Skeleton className="h-44 w-full rounded-2xl sm:h-56" />
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
          <div className="mt-8 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f0f0f3] dark:bg-[#141416]">
        <p className="text-sm text-stone-500">Learning path not found</p>
      </div>
    );
  }

  const isPlanning = group.generation_status === 'planning';
  const isGenerating = group.generation_status === 'generating';
  const isFailed = group.generation_status === 'failed';
  const isInProgress = isPlanning || isGenerating;
  const genProgress =
    group.target_session_count > 0
      ? Math.round((group.generated_session_count / group.target_session_count) * 100)
      : 0;

  return (
    <div className="min-h-screen bg-[#f0f0f3] text-stone-900 dark:bg-[#141416] dark:text-stone-100">
      {/* Header with animated prompt */}
      <HeaderWithPrompt />

      <motion.div
        className="mx-auto w-full max-w-3xl space-y-6 px-4 py-6 sm:space-y-8 sm:py-8"
        initial={
          shouldReduceMotion
            ? false
            : { opacity: 0, transform: 'translateY(10px) scale(0.985)' }
        }
        animate={{ opacity: 1, transform: 'translateY(0px) scale(1)' }}
        transition={{ duration: 0.24, ease: EASE_OUT }}
      >
        {/* Hero */}
        <div className="relative aspect-video overflow-hidden rounded-2xl shadow-[0_12px_32px_rgba(0,0,0,0.12)]">
          {group.cover_image_url ? (
            <img
              src={group.cover_image_url}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-stone-700 to-stone-900" />
          )}
          <div className="absolute inset-0 bg-black/55" />
          <div className="relative z-10 flex h-full flex-col justify-end p-4 text-white sm:p-6">
            <h1 className="text-xl font-bold tracking-tight sm:text-2xl">{group.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-white/80 sm:gap-4 sm:text-sm">
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {group.total_duration_minutes} min
              </span>
              <span className="flex items-center gap-1">
                <BookOpen className="h-3.5 w-3.5" />
                {group.session_count} sessions
              </span>
              {group.enrollment_count > 0 && (
                <span>{group.enrollment_count} enrolled</span>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="space-y-3">
          <p className="text-sm leading-relaxed text-stone-600 dark:text-stone-400">{group.description}</p>
        </div>

        {/* Planning / generation progress */}
        {isInProgress && (
          <div className="space-y-2 rounded-2xl bg-[#f0f0f3] p-4 shadow-[inset_3px_3px_8px_rgba(0,0,0,0.06),inset_-2px_-2px_6px_rgba(255,255,255,0.8)] dark:bg-[#1c1c1f] dark:shadow-[inset_3px_3px_8px_rgba(0,0,0,0.45)]">
            <div className="flex items-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin text-stone-500" />
              <span className="text-stone-500">
                {isPlanning
                  ? 'Planning your learning path...'
                  : `Generating session ${group.generated_session_count} of ${group.target_session_count}...`}
              </span>
            </div>
            {isGenerating && <Progress value={genProgress} className="h-1.5" />}
          </div>
        )}

        {/* Failed state */}
        {isFailed && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-700">
              Something went wrong while generating this learning path. Please try again.
            </p>
          </div>
        )}

        {/* CTA button — soft 3D primary action */}
        {!isInProgress && !isFailed && group.sessions.length > 0 && (
          <div className="pt-1">
            <StartLearningCta group={group} />
          </div>
        )}

        {/* Sessions list */}
        <div className="space-y-4">
          <h2 className="text-xs font-medium uppercase tracking-wider text-stone-400">
            Sessions
          </h2>
          <div className="space-y-2.5">
            {group.sessions.map((session, i) => (
              <motion.div
                key={session.id}
                className="flex items-start gap-3 rounded-2xl bg-[#f0f0f3] p-3.5 shadow-[4px_4px_10px_rgba(0,0,0,0.05),-3px_-3px_8px_rgba(255,255,255,0.85)] dark:bg-[#1c1c1f] dark:shadow-[4px_4px_12px_rgba(0,0,0,0.4),-2px_-2px_8px_rgba(255,255,255,0.03)]"
                initial={
                  shouldReduceMotion
                    ? false
                    : { opacity: 0, transform: 'translateY(6px)' }
                }
                animate={{ opacity: 1, transform: 'translateY(0px)' }}
                transition={{
                  duration: 0.2,
                  ease: EASE_OUT,
                  delay: shouldReduceMotion ? 0 : Math.min(i * 0.04, 0.28),
                }}
              >
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#e8e8ec] text-xs font-semibold text-stone-500 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.06),inset_-1px_-1px_3px_rgba(255,255,255,0.8)] dark:bg-[#252528] dark:text-stone-400">
                  {i + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-stone-900 dark:text-stone-100">{session.name}</p>
                  {session.description && (
                    <p className="mt-0.5 line-clamp-2 text-xs text-stone-500">
                      {session.description}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-stone-400">{session.duration} min</p>
                </div>
              </motion.div>
            ))}
            {isInProgress && group.sessions.length < (group.target_session_count || 1) && (
              <div className="flex items-center gap-3 rounded-2xl border border-dashed border-stone-300 p-3.5 dark:border-stone-700">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#e8e8ec] dark:bg-[#252528]">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-stone-400" />
                </div>
                <p className="text-xs text-stone-400">
                  Sessions being generated...
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Learning outcomes — only meaningful for learning paths, not screening/research groups */}
        {(group.type ?? 'learning') === 'learning' && group.learning_outcomes.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xs font-medium uppercase tracking-wider text-stone-400">
              What you'll achieve
            </h2>
            <div className="space-y-2">
              {group.learning_outcomes.map((outcome, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                  <p className="text-sm text-stone-700 dark:text-stone-300">{outcome}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Materials slider */}
        {group.sessions.length > 0 && materialsStatus.hasAnyMaterials && (
          <MaterialsSlider
            sessionMaterials={materialsStatus.sessionMaterials}
            totalImages={materialsStatus.totalImages}
            totalProcessingImages={materialsStatus.totalProcessingImages}
          />
        )}
      </motion.div>
    </div>
  );
}
